import React, { useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { api } from "@/lib/api";
import { FileUp, FileDown, FileSpreadsheet, AlertTriangle, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

const API_BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Phase 18 item 4: org CSV bridges — fixed-template import with per-row
// validation preview + partial publish, and a round-trippable export.
export function OrgCsvBridge() {
  const fileRef = useRef(null);
  const [csvText, setCsvText] = useState(null);
  const [fileName, setFileName] = useState("");
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const onFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setFileName(f.name);
    setResult(null);
    const reader = new FileReader();
    reader.onload = async () => {
      const text = String(reader.result || "");
      setCsvText(text);
      setBusy(true);
      try {
        setPreview(await api.orgCsvPreview(text));
      } catch (err) {
        setPreview(null);
        toast.error(err?.response?.data?.detail || "Could not read that file");
      }
      setBusy(false);
    };
    reader.readAsText(f);
    e.target.value = ""; // same file can be re-picked after fixing it
  };

  const publish = async () => {
    if (!csvText) return;
    setBusy(true);
    try {
      const r = await api.orgCsvPublish(csvText);
      setResult(r);
      setPreview(null);
      setCsvText(null);
      toast.success(`${r.published} opportunit${r.published === 1 ? "y" : "ies"} published${r.failed.length ? ` — ${r.failed.length} row${r.failed.length === 1 ? "" : "s"} skipped` : ""}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Import failed");
    }
    setBusy(false);
  };

  const exportCsv = async () => {
    try {
      const blob = await api.orgCsvExport();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "civichearth-opportunities.csv";
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Export failed");
    }
  };

  return (
    <div className="mt-12" data-testid="org-csv-bridge">
      <h2 className="font-display text-2xl text-forest tracking-tight flex items-center gap-2">
        <FileSpreadsheet className="w-5 h-5 text-terracotta" /> Bring your opportunities with you
      </h2>
      <p className="text-sm text-neutral-600 mt-1 max-w-2xl">
        Coming from another system? Export your opportunities there and match
        the columns to our template — valid rows publish straight away, and
        anything we can't read is reported line by line.
      </p>
      <div className="flex flex-wrap gap-2 mt-4">
        <Button asChild variant="outline" className="rounded-full h-9 px-4 text-sm" data-testid="csv-template-btn">
          <a href={`${API_BASE}/org/import-template.csv`} download>
            <FileDown className="w-4 h-4 mr-1.5" /> Download template
          </a>
        </Button>
        <Button
          variant="outline"
          className="rounded-full h-9 px-4 text-sm"
          data-testid="csv-upload-btn"
          disabled={busy}
          onClick={() => fileRef.current?.click()}
        >
          <FileUp className="w-4 h-4 mr-1.5" /> Import opportunities (CSV)
        </Button>
        <Button variant="outline" className="rounded-full h-9 px-4 text-sm" data-testid="csv-export-btn" onClick={exportCsv}>
          <FileDown className="w-4 h-4 mr-1.5" /> Export opportunities (CSV)
        </Button>
        <input ref={fileRef} type="file" accept=".csv,text/csv" className="hidden" onChange={onFile} data-testid="csv-file-input" />
      </div>

      {preview && (
        <div className="mt-5 bg-white border border-neutral-200 rounded-xl p-5" data-testid="csv-preview">
          <div className="flex items-center gap-3 flex-wrap">
            <span className="text-sm font-medium text-forest">{fileName}</span>
            <Badge className="bg-forest/10 text-forest border-none">{preview.valid_count} ready</Badge>
            {preview.error_count > 0 && (
              <Badge className="bg-red-100 text-red-700 border-none" data-testid="csv-error-count">{preview.error_count} with errors</Badge>
            )}
            {preview.duplicate_count > 0 && (
              <Badge className="bg-amber-100 text-amber-800 border-none" data-testid="csv-duplicate-count">{preview.duplicate_count} possible duplicate{preview.duplicate_count === 1 ? "" : "s"}</Badge>
            )}
          </div>
          <div className="mt-3 max-h-72 overflow-y-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs uppercase tracking-wide text-neutral-500 border-b border-neutral-100">
                  <th className="py-1.5 pr-3">Line</th>
                  <th className="py-1.5 pr-3">Title</th>
                  <th className="py-1.5 pr-3">Date</th>
                  <th className="py-1.5">Status</th>
                </tr>
              </thead>
              <tbody>
                {preview.rows.map((r) => (
                  <tr key={r.line} className="border-b border-neutral-50" data-testid={`csv-row-${r.line}`}>
                    <td className="py-1.5 pr-3 text-neutral-500">{r.line}</td>
                    <td className="py-1.5 pr-3 text-forest">{r.title || <span className="text-neutral-400 italic">—</span>}</td>
                    <td className="py-1.5 pr-3 text-neutral-600">{r.event_date || "flexible"}</td>
                    <td className="py-1.5">
                      {r.ok ? (
                        <span className="inline-flex items-center gap-1 text-forest text-xs">
                          <CheckCircle2 className="w-3.5 h-3.5" /> Ready{r.duplicate && <span className="text-amber-700 ml-1" data-testid={`csv-dup-${r.line}`}>· possible duplicate</span>}
                        </span>
                      ) : (
                        <span className="inline-flex items-start gap-1 text-red-700 text-xs" data-testid={`csv-errors-${r.line}`}>
                          <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" /> {r.errors.join("; ")}
                        </span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="flex items-center gap-3 mt-4">
            <Button
              data-testid="csv-publish-btn"
              disabled={busy || preview.valid_count === 0}
              onClick={publish}
              className="rounded-full bg-forest hover:bg-forest-hover h-9 px-5 text-sm"
            >
              Publish {preview.valid_count} row{preview.valid_count === 1 ? "" : "s"}
            </Button>
            <button className="text-sm text-neutral-500 underline underline-offset-2" onClick={() => { setPreview(null); setCsvText(null); }}>
              Cancel
            </button>
            {preview.error_count > 0 && (
              <span className="text-xs text-neutral-500">Rows with errors are skipped — fix and re-import them any time.</span>
            )}
          </div>
        </div>
      )}

      {result && (
        <div className="mt-4 bg-forest/5 border border-forest/15 rounded-xl px-4 py-3 text-sm text-forest" data-testid="csv-result">
          <CheckCircle2 className="w-4 h-4 inline mr-1.5 -mt-0.5" />
          {result.published} published{result.failed.length > 0 && `, ${result.failed.length} skipped (lines ${result.failed.map(f => f.line).join(", ")})`}.
        </div>
      )}
    </div>
  );
}
